/**
 * 前端空闲超时自动登出
 *
 * 等保三级要求：登录连接超时自动退出。
 * 监听鼠标/键盘/触摸/滚轮（capture）等事件，超过指定时间无操作后自动清除 Token 并跳转登录页。
 * 自动登出前弹出 ElMessageBox 提示；弹窗 z-index 须高于业务全屏层，避免「继续使用」点击落到下层。
 * 警告显示后：滚轮/鼠标等会顺延宽限期；内部滚动区依赖 wheel 捕获（scroll 不冒泡到 document）。
 */

import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { ElMessageBox, type ElMessageBoxOptions } from "element-plus";
import { useSimpleAuthStore } from "@/stores";

/** 扩展 ElMessageBoxOptions 以支持 modalClass（Element Plus 实际支持，但类型定义可能落后） */
interface IdleTimeoutMessageBoxOptions extends ElMessageBoxOptions {
  modalClass?: string;
  customClass?: string;
}

// 等保三级要求：连续无操作自动超时。临床工作站保留 15 分钟提醒等待时间，避免医生书写病历时频繁被打断。
const IDLE_LIMIT_MS = 30 * 60 * 1000;
const WARNING_BEFORE_MS = 15 * 60 * 1000;

/** 遮罩自定义类名，配合 App.vue 全局样式抬高 z-index（Element Plus 打开时会 nextZIndex() 覆盖 options.zIndex） */
const IDLE_MSGBOX_MODAL_CLASS = "pgy-idle-timeout-msgbox-overlay";
const IDLE_MSGBOX_CLASS = "pgy-idle-timeout-msgbox";

export function useIdleTimeout() {
  const router = useRouter();
  const authStore = useSimpleAuthStore();
  /** 警告弹出后的「宽限期」登出计时；平时为 null，不再使用「从上次活动起固定 15 分钟」与警告弹窗并行的第二支计时器（避免竞态） */
  let graceLogoutTimer: ReturnType<typeof setTimeout> | null = null;
  let warningTimer: ReturnType<typeof setTimeout> | null = null;
  let warningShown = ref(false);
  let destroyed = false;

  const clearGraceTimer = () => {
    if (graceLogoutTimer) {
      clearTimeout(graceLogoutTimer);
      graceLogoutTimer = null;
    }
  };

  /** 警告已显示时：滚动/滚轮等仍视为「仍在使用」，顺延宽限期（内部滚动区不会冒泡 document 的 scroll） */
  const extendGraceLogoutTimer = () => {
    if (!warningShown.value || destroyed) return;
    clearGraceTimer();
    graceLogoutTimer = setTimeout(() => {
      void doLogout();
    }, WARNING_BEFORE_MS);
  };

  const resetTimers = () => {
    if (destroyed) return;
    clearGraceTimer();
    if (warningTimer) clearTimeout(warningTimer);
    warningShown.value = false;

    warningTimer = setTimeout(() => {
      if (destroyed) return;
      warningShown.value = true;
      extendGraceLogoutTimer();

      ElMessageBox.confirm(
        "您已长时间未操作，系统将在 15 分钟后自动登出。",
        "空闲提醒",
        {
          confirmButtonText: "继续使用",
          cancelButtonText: "立即登出",
          type: "warning",
          closeOnClickModal: false,
          modalClass: IDLE_MSGBOX_MODAL_CLASS,
          customClass: IDLE_MSGBOX_CLASS,
        } as IdleTimeoutMessageBoxOptions,
      )
        .then(() => {
          warningShown.value = false;
          clearGraceTimer();
          resetTimers();
        })
        .catch(() => {
          warningShown.value = false;
          clearGraceTimer();
          void doLogout();
        });
    }, IDLE_LIMIT_MS - WARNING_BEFORE_MS);
  };

  const doLogout = async () => {
    await authStore.logout({ suppressMessage: true });
    router.replace({ name: "Login" }).catch(() => {
      window.location.href = "/login";
    });
  };

  const EVENTS = [
    "mousemove",
    "mousedown",
    "keydown",
    "touchstart",
    "scroll",
  ] as const;

  const onActivity = () => {
    if (destroyed) return;
    if (warningShown.value) {
      extendGraceLogoutTimer();
      return;
    }
    resetTimers();
  };

  onMounted(() => {
    if (!authStore.token) return;
    EVENTS.forEach((e) =>
      document.addEventListener(e, onActivity, { passive: true }),
    );
    document.addEventListener("wheel", onActivity, {
      passive: true,
      capture: true,
    });
    resetTimers();
  });

  onUnmounted(() => {
    destroyed = true;
    clearGraceTimer();
    if (warningTimer) clearTimeout(warningTimer);
    EVENTS.forEach((e) => document.removeEventListener(e, onActivity));
    document.removeEventListener("wheel", onActivity, { capture: true });
  });
}
